// frontend/src/pages/Home.jsx

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import Slider from "react-slick";

// Heroicons (24/outline)
import {
  CurrencyRupeeIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  BanknotesIcon,
  GiftIcon,
  LockClosedIcon,
  CheckBadgeIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  SparklesIcon,
  RocketLaunchIcon,
  GlobeAltIcon,
  BoltIcon,
  ChartBarIcon,
  LifebuoyIcon,
  CreditCardIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function Home() {
  const navigate = useNavigate();
  const [cryptoData, setCryptoData] = useState([]);
  const [user, setUser] = useState(null);
  const [mdUpdatedAt, setMdUpdatedAt] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [openFAQ, setOpenFAQ] = useState(null);

  // whoami
  useEffect(() => {
    axios
      .get("/whoami")
      .then((res) => setUser(res.data.user))
      .catch(() => setUser(null));
  }, []);

  // market data
  useEffect(() => {
    fetch(
      "https://api.coingecko.com/api/v3/coins/markets?vs_currency=inr&order=market_cap_desc&per_page=8&page=1&sparkline=true"
    )
      .then((res) => res.json())
      .then((data) => {
        setCryptoData(Array.isArray(data) ? data : []);
        setMdUpdatedAt(new Date());
      })
      .catch((err) => console.error("Error fetching coin data:", err));
  }, []);

  // Sliders
  const heroSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 7000,
    speed: 800,
    cssEase: "ease-in-out",
    pauseOnHover: false,
    responsive: [{ breakpoint: 480, settings: { autoplaySpeed: 8000 } }],
  };

  const testimonialsSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 6500,
    speed: 700,
    cssEase: "ease",
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  // Slides (classic full-bleed gradient hero with centered content)
  // IMPORTANT: updated routes so they point to existing pages:
  // - "Learn Withdrawals" -> "/withdraw" (instead of "/withdraw-info")
  // - "Security Practices" -> "/support" (instead of "/security")
  const slides = [
    {
      title: "Trade Crypto with Confidence",
      subtitleLines: [
        "FIU registration in process",
        "Built for India with bank-grade security and a mobile-first experience.",
      ],
      gradient: "from-sky-500 via-blue-600 to-indigo-700",
      buttons: !user
        ? [
            { text: "Sign Up", route: "/signup" },
            { text: "Login", route: "/login" },
          ]
        : [{ text: "Go to Dashboard", route: "/dashboard" }],
    },
    {
      title: "Powerful Exchange, Simple UX",
      subtitle:
        "Deep liquidity, instant order execution, and clear fees—no surprises.",
      gradient: "from-indigo-600 via-violet-600 to-blue-700",
      buttons: [
        {
          text: user ? "Start Trading" : "Get Started",
          route: user ? "/quick-trade" : "/signup",
        },
      ],
    },
    {
      title: "Withdrawals That Just Work",
      subtitle:
        "Instant & standard INR payouts direct to your bank. Transparent timelines.",
      gradient: "from-blue-600 via-sky-600 to-indigo-700",
      buttons: [{ text: "Learn Withdrawals", route: "/withdraw" }], // ← fixed
    },
    {
      title: "Security First, Always",
      subtitle:
        "Multi-layer encryption, rigorous security reviews, and insured custody partners.",
      gradient: "from-indigo-700 via-indigo-600 to-blue-700",
      buttons: [{ text: "Security Practices", route: "/support" }], // ← fixed
    },
    {
      title: "Support That Actually Helps",
      subtitle: "Humans, not bots. Resolve issues fast via chat & email.",
      gradient: "from-blue-700 via-blue-600 to-indigo-700",
      buttons: [{ text: "Contact Support", route: "/support" }],
    },
  ];

  const topRows = useMemo(
    () =>
      (cryptoData || []).slice(0, 6).map((c) => ({
        id: c.id,
        name: c.name,
        symbol: (c.symbol || "").toUpperCase(),
        price: c.current_price,
        change: c.price_change_percentage_24h,
        image: c.image,
      })),
    [cryptoData]
  );

  const testimonials = [
    {
      name: "Rahul S.",
      title: "Algo Trader",
      quote:
        "Execution is lightning fast. The INR flows are reliable and the fees are predictable.",
    },
    {
      name: "Priya K.",
      title: "First-time Investor",
      quote:
        "UI is clean, deposits were instant, and support actually solved my question in minutes.",
    },
    {
      name: "Ankit T.",
      title: "Power User",
      quote:
        "Charts, P2P, and quick trade—everything I need without getting lost in menus.",
    },
    {
      name: "Neha M.",
      title: "Long-term Holder",
      quote:
        "Security practices are solid and transparent; I feel safe keeping funds here.",
    },
  ];

  const faqs = [
    {
      q: "Is Coinzetto compliant in India?",
      a: "Yes—FIU registration is in process and we follow strict KYC/AML procedures aligned with Indian regulations.",
    },
    {
      q: "How fast are INR deposits/withdrawals?",
      a: "Deposits are typically instant. Withdrawals support instant and standard modes with clear ETAs before you confirm.",
    },
    {
      q: "What are your trading fees?",
      a: "We keep fees transparent and competitive. Exact fee slabs are visible before order placement and on our Fees page.",
    },
    {
      q: "Can I trade on mobile?",
      a: "Absolutely. Our experience is optimized for mobile and works great on all modern browsers.",
    },
  ];

  const submitNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    alert(`Thanks! We'll keep you posted at ${newsletterEmail}.`);
    setNewsletterEmail("");
  };

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-slate-900 pb-20 md:pb-12 relative font-sans">
      <style>
        {`
          @keyframes marquee { 0% { transform: translateX(100vw); } 100% { transform: translateX(-100%); } }
          .animate-marquee { animation: marquee 18s linear infinite; }
          @media (max-width: 640px) {
            .slick-dots li button:before { font-size: 8px !important; }
            .slick-dots { bottom: 8px !important; }
          }
          html.dark .slick-dots li button:before { color: rgba(255,255,255,0.7) !important; }
          html.dark .slick-dots li.slick-active button:before { color: #fff !important; }
        `}
      </style>

      {/* ───────────────── Hero (classic slides) ───────────────── */}
      <div className="w-full">
        <Slider {...heroSettings}>
          {slides.map((slide, idx) => (
            <div key={idx}>
              <div
                className={
                  "flex flex-col justify-center items-center text-center text-white h-[360px] sm:h-[420px] md:h-[520px] py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br " +
                  slide.gradient
                }
              >
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-white text-xs font-semibold mb-3 ring-1 ring-white/20">
                  <SparklesIcon className="h-4 w-4" />
                  {user ? "Welcome back" : "New to Coinzetto?"}
                </div>

                <h1 className="text-white text-3xl sm:text-4xl md:text-6xl font-extrabold leading-tight mb-3 md:mb-4 max-w-[1000px] drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]">
                  {slide.title}
                </h1>

                {slide.subtitleLines ? (
                  <>
                    {slide.subtitleLines.map((line, i) => (
                      <p
                        key={i}
                        className={
                          "text-white/90 text-sm sm:text-base md:text-xl font-medium " +
                          (i === slide.subtitleLines.length - 1
                            ? "mb-5 md:mb-6 max-w-xl sm:max-w-2xl"
                            : "mb-1.5 sm:mb-2")
                        }
                      >
                        {line}
                      </p>
                    ))}
                  </>
                ) : (
                  slide.subtitle && (
                    <p className="text-white/90 text-sm sm:text-base md:text-xl font-medium mb-5 md:mb-6 max-w-xl sm:max-w-2xl">
                      {slide.subtitle}
                    </p>
                  )
                )}

                <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3">
                  {slide.buttons.map((btn, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(btn.route)}
                      className="bg-white text-blue-700 font-bold px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 rounded-full shadow-md hover:bg-blue-50 transition text-sm sm:text-base"
                    >
                      {btn.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      {/* ───────────────── Scrolling Notice ───────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm w-full relative overflow-hidden h-9 sm:h-10 md:h-12">
        <div className="absolute inset-y-0 left-0 flex items-center whitespace-nowrap animate-marquee px-3 md:px-4">
          <CheckCircleIcon className="inline-block h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 mr-2" />
          <span className="text-[11px] sm:text-xs md:text-sm text-blue-600 dark:text-blue-300 tracking-wide uppercase">
            FIU Registration in Process • Compliant with Indian Laws • Bank-grade Security
          </span>
        </div>
      </div>

      {/* ───────────────── Logos / Social Proof ───────────────── */}
      <section className="max-w-7xl mx-auto w-full md:px-6 px-3 mt-4 sm:mt-6">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow p-3 sm:p-4 md:p-6 border-t-4 border-blue-600">
          <p className="text-center text-[11px] sm:text-xs md:text-sm text-blue-700 dark:text-blue-300 mb-2 sm:mb-3">
            Trusted by traders and builders across India
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 md:gap-6 items-center">
            {["FinTech Daily", "CryptoWire", "DeFi Times", "BlockNews", "India Tech"].map(
              (brand) => (
                <BrandPill key={brand} label={brand} />
              )
            )}
          </div>
        </div>
      </section>

      {/* ───────────────── Main content ───────────────── */}
      <main className="max-w-7xl mx-auto w-full md:px-6 px-3 md:mt-6 mt-3 md:space-y-8 space-y-3">
        {/* Quick Actions */}
        <section className="bg-white dark:bg-slate-800 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 md:flex md:items-center md:justify-between md:gap-4 border-t-4 md:border-t-0 md:border-l-4 border-blue-500">
          <p className="text-blue-700 dark:text-blue-300 font-medium md:mb-0 mb-2 text-sm md:text-base">
            Quick Actions
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 md:gap-3 md:ml-auto">
            {!user ? (
              <>
                <button
                  onClick={() => navigate("/signup")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  Sign Up
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  Login
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/deposit")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  Deposit
                </button>
                <button
                  onClick={() => navigate("/withdraw")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  Withdraw
                </button>
                <button
                  onClick={() => navigate("/quick-trade")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  Quick Trade
                </button>
                <button
                  onClick={() => navigate("/p2p")}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
                >
                  P2P Market
                </button>
              </>
            )}
          </div>
        </section>

        {/* ⭐ P2P PROMO — Bigger heading + direct link */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-600">
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-center">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-wider text-blue-600 dark:text-blue-300 font-semibold">
                Peer-to-Peer
              </p>
              <h2 className="mt-1 text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-blue-800 dark:text-slate-100">
                P2P Market — The Fastest Way to Buy/Sell Crypto in INR
              </h2>
              <p className="mt-2 text-sm sm:text-base text-blue-700 dark:text-slate-300">
                Escrow-protected trades, instant matching, transparent fees.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => navigate("/p2p")}
                  className="w-full sm:w-auto bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow hover:bg-blue-700 active:scale-[0.99] transition"
                >
                  Go to P2P
                </button>
                <button
                  onClick={() => navigate("/p2p")}
                  className="w-full sm:w-auto bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-50 dark:hover:bg-slate-900/70 transition"
                >
                  Browse Offers
                </button>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-slate-900 rounded-xl border border-blue-100 dark:border-slate-700 p-4">
              <div className="grid grid-cols-2 gap-3">
                <P2PBadge icon={ShieldCheckIcon} label="Escrow Protected" />
                <P2PBadge icon={BoltIcon} label="Avg Match &lt; 2 min" />
                <P2PBadge icon={CurrencyRupeeIcon} label="INR & USDT" />
                <P2PBadge icon={GlobeAltIcon} label="Pan-India Coverage" />
              </div>
              <div className="mt-4 h-1.5 bg-blue-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 w-5/6" />
              </div>
              <p className="mt-2 text-xs text-blue-700 dark:text-slate-400">
                Trade directly with peers—fast, fair, and secure.
              </p>
            </div>
          </div>
        </section>

        {/* Feature Trio */}
        <section className="grid md:grid-cols-3 grid-cols-1 gap-3 md:gap-6">
          <ValueCard
            icon={ShieldCheckIcon}
            title="Secure & Compliant"
            text="Bank-grade encryption, strict KYC/AML, and policy-driven access."
          />
          <ValueCard
            icon={ChartBarIcon}
            title="Pro Tools, Simple UX"
            text="Real-time charts, INR markets, and a clean mobile-first design."
          />
          <ValueCard
            icon={LifebuoyIcon}
            title="Human Support"
            text="Fast, friendly answers over chat and email—24×7."
          />
        </section>

        {/* Trending Cryptos — bigger coins */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100">
              Trending Cryptos
            </h3>
            {mdUpdatedAt && (
              <span className="text-[11px] sm:text-xs text-gray-500">
                Last update {mdUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-5">
            {topRows.map((coin) => (
              <div
                key={coin.id}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow border border-gray-100 dark:border-slate-700 p-3 sm:p-5 hover:shadow-xl transition"
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <img
                    src={coin.image}
                    alt={coin.name}
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-14 md:w-14 rounded-full ring-1 ring-blue-100 dark:ring-slate-700"
                  />
                  <div className="min-w-0">
                    <div className="text-sm sm:text-base md:text-lg font-semibold text-blue-800 dark:text-slate-100 truncate">
                      {coin.name}
                    </div>
                    <div className="text-[11px] sm:text-xs md:text-sm text-blue-600 dark:text-slate-300">
                      {coin.symbol}
                    </div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-sm sm:text-base md:text-lg font-semibold text-blue-800 dark:text-slate-100">
                      {formatINR(coin.price)}
                    </div>
                    <div
                      className={
                        "text-[11px] sm:text-xs md:text-sm font-semibold " +
                        (coin.change >= 0 ? "text-green-600" : "text-red-600")
                      }
                    >
                      {coin.change >= 0 ? "+" : ""}
                      {Number(coin.change || 0).toFixed(2)}%
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="h-1.5 bg-blue-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={
                        "h-full " + (coin.change >= 0 ? "bg-green-500" : "bg-red-500")
                      }
                      style={{
                        width: `${Math.min(100, Math.abs(coin.change || 0))}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}

            {topRows.length === 0 && (
              <div className="col-span-2 sm:col-span-3 text-center text-blue-600 dark:text-slate-300 py-6 sm:py-8">
                Loading coins…
              </div>
            )}
          </div>
        </section>

        {/* Market Overview */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100">
              Market Overview
            </h3>
            {mdUpdatedAt && (
              <span className="text-[11px] sm:text-xs text-gray-500">
                Last update {mdUpdatedAt.toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Mobile: card list */}
          <div className="sm:hidden grid grid-cols-1 gap-2">
            {topRows.length > 0 ? (
              topRows.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-blue-100 dark:border-slate-700 bg-blue-50/40 dark:bg-slate-900 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[13px] font-semibold text-blue-900 dark:text-slate-100">
                      {item.name}
                    </div>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                        item.change >= 0
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {item.change >= 0 ? "+" : ""}
                      {Number(item.change || 0).toFixed(2)}%
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-blue-700 dark:text-slate-300">
                    {item.symbol}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-blue-900 dark:text-slate-100">
                    {Number(item.price || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-blue-600 dark:text-slate-300 py-6">
                Loading market data…
              </div>
            )}
          </div>

          {/* Tablet/Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full table-auto text-blue-800 dark:text-slate-100">
              <thead>
                <tr className="bg-blue-50/70 dark:bg-slate-900/60">
                  <th className="px-4 py-2 text-left text-sm font-medium">Coin</th>
                  <th className="px-4 py-2 text-left text-sm font-medium">Symbol</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Price (₹)</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">24h Change</th>
                </tr>
              </thead>
              <tbody>
                {topRows.length > 0 ? (
                  topRows.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-slate-700 last:border-b-0 hover:bg-blue-50/60 dark:hover:bg-slate-900/60 transition"
                    >
                      <td className="px-4 py-3">{item.name}</td>
                      <td className="px-4 py-3">{item.symbol}</td>
                      <td className="px-4 py-3 text-right">
                        {Number(item.price || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            item.change >= 0
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {item.change >= 0 ? "+" : ""}
                          {Number(item.change || 0).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-blue-600 dark:text-slate-300">
                      Loading market data…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* How it Works — UPDATED FLOW */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            How it Works
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
            <StepCard step="1" title="Create Account & Verify" icon={CheckBadgeIcon} />
            <StepCard step="2" title="Deposit/Withdraw INR or Crypto" icon={BanknotesIcon} />
            <StepCard step="3" title="Start Trading (P2P & Exchange)" icon={ChartBarIcon} />
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100">
              Loved by Traders
            </h3>
            <div className="hidden sm:flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
              <SparklesIcon className="h-4 w-4" />
              Real feedback from our community
            </div>
          </div>
          <Slider {...testimonialsSettings}>
            {testimonials.map((t, i) => (
              <div key={i} className="px-2">
                <TestimonialCard {...t} />
              </div>
            ))}
          </Slider>
        </section>

        {/* App Promo / CTA */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100">
                Trade on the go
              </h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-slate-300">
                Coinzetto is optimized for mobile—fast, responsive, and secure on
                every device.
              </p>
              <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => navigate("/quick-trade")}
                  className="w-full sm:w-auto bg-blue-600 text-white font-semibold px-4 py-2 rounded-xl hover:bg-blue-700 active:scale-[0.99] transition text-sm"
                >
                  Open Web App
                </button>
                <button
                  onClick={() => navigate("/support")}
                  className="w-full sm:w-auto bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700 font-semibold px-4 py-2 rounded-xl hover:bg-blue-50 dark:hover:bg-slate-900/70 transition text-sm"
                >
                  Get Help
                </button>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-slate-900 border border-blue-100 dark:border-slate-700 rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-3">
                <DevicePhoneMobileIcon className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 dark:text-blue-400" />
                <div>
                  <div className="text-sm font-semibold text-blue-800 dark:text-slate-100">
                    Optimized for Mobile
                  </div>
                  <div className="text-xs text-blue-700 dark:text-slate-400">
                    Clean, uncluttered interface
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3 text-center">
                <MiniStat label="Avg. Load" value="~1.2s" />
                <MiniStat label="Uptime" value="99.95%" />
                <MiniStat label="Support" value="24×7" />
              </div>
            </div>
          </div>
        </section>

        {/* Follow Us */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow p-4 sm:p-6 border-t-4 border-blue-600 text-center">
          <h4 className="text-base sm:text-lg font-semibold text-blue-800 dark:text-slate-100">
            Follow us on
          </h4>
          <a
            href="https://www.coinzett.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-slate-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700 hover:bg-blue-100 dark:hover:bg-slate-900/70"
          >
            www.coinzett.com
          </a>
        </section>

        {/* FAQ */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100 mb-3 sm:mb-4">
            Frequently Asked Questions
          </h3>
          <div className="divide-y divide-blue-100 dark:divide-slate-700 rounded-xl border border-blue-100 dark:border-slate-700 overflow-hidden">
            {faqs.map((item, i) => (
              <button
                key={i}
                className="w-full text-left p-3 sm:p-4 hover:bg-blue-50/50 dark:hover:bg-slate-900/60 transition"
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-semibold text-blue-800 dark:text-slate-100 text-sm sm:text-base">
                    {item.q}
                  </span>
                  <span className="text-blue-700 dark:text-blue-300 text-sm">
                    {openFAQ === i ? "—" : "+"}
                  </span>
                </div>
                {openFAQ === i && (
                  <p className="mt-2 text-[13px] sm:text-sm text-blue-700 dark:text-slate-300">
                    {item.a}
                  </p>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 sm:p-6 border-t-4 border-blue-500">
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4 items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-slate-100">
                Stay in the loop
              </h3>
              <p className="text-[13px] sm:text-sm text-blue-700 dark:text-slate-300">
                Market insights, security updates, and feature drops—no spam.
              </p>
            </div>
            <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 rounded-lg border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-blue-800 dark:text-slate-100 placeholder:text-blue-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 active:scale-[0.99] transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-5 sm:p-6 border-t-4 border-blue-600 text-center">
          <h3 className="text-xl sm:text-2xl font-bold text-blue-800 dark:text-slate-100">
            Ready to trade with confidence?
          </h3>
          <p className="mt-1 text-[13px] sm:text-sm text-blue-700 dark:text-slate-300">
            Join thousands of traders who choose clarity, speed, and security.
          </p>
          <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
            <button
              onClick={() => navigate(user ? "/quick-trade" : "/signup")}
              className="w-full sm:w-auto bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow hover:bg-blue-700 active:scale-[0.99] transition"
            >
              {user ? "Start Trading" : "Create Free Account"}
            </button>
            <button
              onClick={() => navigate("/about")}
              className="w-full sm:w-auto bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-slate-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-50 dark:hover:bg-slate-900/70 transition"
            >
              Learn More
            </button>
          </div>
        </section>
      </main>

      {/* Sticky mobile CTA bar (phones only) */}
      <div className="fixed bottom-2 left-0 right-0 z-40 px-3 sm:hidden">
        <div className="rounded-2xl shadow-lg border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 flex gap-2">
          <button
            onClick={() => navigate(user ? "/quick-trade" : "/signup")}
            className="flex-1 bg-blue-600 text-white font-semibold py-2 rounded-xl active:scale-[0.99]"
          >
            {user ? "Quick Trade" : "Get Started"}
          </button>
          <button
            onClick={() => navigate("/p2p")}
            className="flex-1 border border-blue-200 dark:border-slate-700 text-blue-700 dark:text-blue-300 bg-white dark:bg-slate-900 font-semibold py-2 rounded-xl"
          >
            P2P
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Reusable Bits
 * ────────────────────────────────────────────────────────────────────────────*/

function BrandPill({ label }) {
  return (
    <div className="h-9 sm:h-10 md:h-12 bg-blue-50 dark:bg-slate-900 text-blue-700 dark:text-blue-300 rounded-lg flex items-center justify-center border border-blue-100 dark:border-slate-700 text-[11px] sm:text-xs md:text-sm font-semibold">
      {label}
    </div>
  );
}

function ValueCard({ icon: Icon, title, text }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow p-4 sm:p-5 border-t-4 border-blue-600">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-blue-50 dark:bg-slate-900 flex items-center justify-center ring-1 ring-blue-100 dark:ring-slate-700">
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="text-base sm:text-lg font-semibold text-blue-800 dark:text-slate-100">
          {title}
        </div>
      </div>
      <p className="mt-2 text-[13px] sm:text-sm text-blue-700 dark:text-slate-300">{text}</p>
    </div>
  );
}

function StepCard({ step, title, icon: Icon }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow p-3 sm:p-4 border border-blue-100 dark:border-slate-700">
      <div className="flex items-center justify-between">
        <span className="h-7 w-7 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">
          {step}
        </span>
        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="mt-3 text-blue-800 dark:text-slate-100 font-semibold text-sm sm:text-base">
        {title}
      </div>
      <div className="mt-2 h-1 bg-blue-100 dark:bg-slate-700 rounded-full">
        <div className="h-full w-2/3 bg-blue-600 rounded-full" />
      </div>
    </div>
  );
}

function TestimonialCard({ name, title, quote }) {
  return (
    <div className="h-full bg-white dark:bg-slate-900 rounded-2xl shadow border border-blue-100 dark:border-slate-700 p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-blue-100 dark:bg-slate-800 flex items-center justify-center">
          <span className="text-blue-700 dark:text-blue-300 font-bold">
            {name?.charAt(0)}
          </span>
        </div>
        <div>
          <div className="text-[13px] sm:text-sm font-semibold text-blue-800 dark:text-slate-100">
            {name}
          </div>
          <div className="text-[11px] sm:text-xs text-blue-700 dark:text-slate-400">
            {title}
          </div>
        </div>
      </div>
      <p className="mt-3 text-[13px] sm:text-sm text-blue-700 dark:text-slate-300 leading-relaxed">
        “{quote}”
      </p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-blue-100 dark:border-slate-700 p-3">
      <div className="text-[11px] text-blue-700 dark:text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-blue-800 dark:text-slate-100">
        {value}
      </div>
    </div>
  );
}

function P2PBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-2.5 py-2 ring-1 ring-blue-100 dark:ring-slate-700">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      <span className="text-sm text-blue-800 dark:text-slate-100">{label}</span>
    </div>
  );
}
